import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Tag from "@/components/base/Tag";
import { getContentById, getRelated, typeLabel, formatDate } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";

const typeVariant: Record<string, "accent" | "primary" | "secondary"> = {
  news: "accent",
  work: "primary",
  analysis: "secondary",
};

export default function DetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const item = getContentById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);

  const related = useMemo(() => (item ? getRelated(item, 10) : []), [item]);

  usePageMeta({
    title: item ? `${item.title} - 星空动漫` : "内容详情 - 星空动漫",
    description: item ? item.summary : "星空动漫内容详情页。",
    keywords: item ? item.tags.join(",") : "星空动漫",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-page flex-col items-center px-4 py-24 text-center md:px-6">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
          <h1 className="mt-4 text-xl font-semibold text-foreground-900">内容不存在或已下线</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的链接可能已经失效。</p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </button>
        </div>
      </SiteLayout>
    );
  }

  const favorited = isFavorite(item.id);

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const breadcrumb = [
    { label: "首页", to: "/" },
    {
      label: typeLabel(item.type),
      to: item.type === "news" ? "/news" : item.type === "work" ? "/works" : "/analysis",
    },
    { label: item.title },
  ];

  return (
    <SiteLayout>
      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <Breadcrumb items={breadcrumb} />

        <article className="mt-4">
          <div className="relative h-[260px] w-full overflow-hidden rounded-lg md:h-[420px]">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title}｜星空动漫`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/45 to-primary-950/10" />
            <div className="absolute inset-0 flex items-end">
              <div className="w-full px-5 pb-6 md:px-8 md:pb-8">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Tag variant={typeVariant[item.type] || "secondary"}>{typeLabel(item.type)}</Tag>
                  <Tag variant="accent">{item.category}</Tag>
                </div>
                <h1 className="max-w-3xl text-2xl font-bold leading-snug text-background-50 md:text-4xl">
                  {item.title}
                </h1>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-line" />
              {item.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {formatDate(item.date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-price-tag-3-line" />
              {item.category}
            </span>
            {item.type === "work" ? (
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500" />
                {item.rating.toFixed(1)} 分
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-fire-line text-accent-500" />
              热度 {item.popularity}
            </span>

            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleFavorite(item.id)}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                  favorited
                    ? "border-accent-400 bg-accent-100 text-accent-800"
                    : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                }`}
                title="收藏（本地演示，仅保存在当前浏览器）"
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-sm font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-link" />
                复制链接
              </button>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                  aria-label="分享到微博"
                >
                  <i className="ri-weibo-line text-lg" />
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                  aria-label="分享到微信"
                >
                  <i className="ri-wechat-line text-lg" />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr]">
            <aside className="lg:sticky lg:top-20 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <button
                  type="button"
                  onClick={() => setTocOpen((prev) => !prev)}
                  className="flex w-full cursor-pointer items-center justify-between text-sm font-semibold text-foreground-900 lg:cursor-default"
                >
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-list-check-2 text-accent-500" />
                    目录导航
                  </span>
                  <i
                    className={`${tocOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} lg:hidden`}
                  />
                </button>
                <nav
                  className={`${tocOpen ? "block" : "hidden"} mt-3 lg:block`}
                  aria-label="文章目录"
                >
                  <ol className="flex flex-col gap-1">
                    {item.sections.map((section, index) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          onClick={() => setTocOpen(false)}
                          className="flex cursor-pointer gap-2 rounded-md px-2 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
                        >
                          <span className="text-accent-600">{String(index + 1).padStart(2, "0")}</span>
                          <span className="line-clamp-2 leading-snug">{section.heading}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-accent-50 p-4">
                <p className="text-xs leading-relaxed text-accent-900">
                  <i className="ri-information-line mr-1" />
                  收藏状态仅保存在当前浏览器本地，用于功能演示。
                </p>
              </div>
            </aside>

            <div className="min-w-0">
              <p className="rounded-lg bg-background-100 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {item.summary}
              </p>

              <div className="mt-6 flex flex-col gap-8">
                {item.sections.map((section, index) => (
                  <section key={section.id} id={section.id} className="scroll-mt-24">
                    <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
                      <span className="font-display text-accent-600">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {section.heading}
                    </h2>
                    <div className="mt-3 flex flex-col gap-3">
                      {section.paragraphs.map((paragraph, pIndex) => (
                        <p
                          key={`${section.id}-${pIndex}`}
                          className="text-[15px] leading-[1.9] text-foreground-700"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200 pt-5">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                  >
                    <i className="ri-price-tag-2-line" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {related.length > 0 ? (
          <section className="mt-14">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
              <span className="text-sm text-foreground-400">根据标签与类别智能匹配</span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {related.map((entry) => (
                <Link
                  key={entry.id}
                  to={`/detail/${entry.id}`}
                  className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                >
                  <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={entry.cover}
                      alt={entry.title}
                      title={`${entry.title}｜星空动漫`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="mb-1 flex items-center gap-2">
                      <Tag variant={typeVariant[entry.type] || "secondary"}>
                        {typeLabel(entry.type)}
                      </Tag>
                    </div>
                    <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                      {entry.title}
                    </h3>
                    <p className="mt-1 line-clamp-1 text-xs text-foreground-500">
                      {formatDate(entry.date)} · {entry.author}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        <div className="mt-10 flex justify-center">
          <div
            className={`pointer-events-none flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-opacity ${
              copied ? "opacity-100" : "opacity-0"
            }`}
          >
            <i className="ri-check-line" />
            链接已复制到剪贴板
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}